import { useMutation, useQueryClient } from '@tanstack/react-query'
import { postRequest, putRequest } from '../api'
import { displayErrorToast } from '../helpers'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { reactivateGuild } from '../api/guild'

interface UpdateEntityOptions<T> {
  entityType: string
  id?: number
  queryKey?: string
  queryKeyValue?: string | number
  parentEntityType?: string
  parentId?: number
  stringifyValues?: (keyof T)[]
  onSuccess?: (data: RecursivePartial<T>) => void
  showSuccessToast?: boolean
}

export const useUpdateTeam = <T extends wowaudit.Team>(
  team: wowaudit.Team,
  onSuccess?: (data: RecursivePartial<T>) => void,
  onError?: (error: Error) => void,
) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: RecursivePartial<T>) => {
      return putRequest(`/api/teams/${team.id}`, { team: data })
    },
    onSuccess: (data: RecursivePartial<T>) => {
      queryClient.setQueryData(['team', { path: team.path }], data)
      toast.success(t('shared.changes-saved'))
      onSuccess?.(data)
    },
    onError: (error) => {
      displayErrorToast(error, t)
      onError?.(error)
    },
  })
}

export const useReactivateGuild = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (guildReference: wowaudit.GuildReference) =>
      reactivateGuild(guildReference.id),
    onSuccess: (data: wowaudit.Guild, guildReference) => {
      queryClient.setQueryData(['guild', { path: guildReference.path }], data)
      queryClient.setQueryData<wowaudit.PageInfo>(['pageInfo'], (pageInfo) =>
        pageInfo?.user
          ? {
              ...pageInfo,
              user: {
                ...pageInfo.user,
                teamReferences: pageInfo.user.teamReferences.map((team) =>
                  team.ownerReference.id === data.id
                    ? {
                        ...team,
                        ownerReference: {
                          ...team.ownerReference,
                          active: data.active,
                        },
                      }
                    : team,
                ),
              },
            }
          : pageInfo,
      )
      toast.success(t('shared.changes-saved'))
    },
    onError: (error) => displayErrorToast(error, t),
  })
}

export const useUpdateEntity = <T>({
  entityType,
  id,
  queryKey = 'id',
  queryKeyValue = id,
  parentEntityType,
  parentId,
  stringifyValues = [],
  onSuccess,
  showSuccessToast = true,
}: UpdateEntityOptions<T>) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const baseUrl = parentEntityType
    ? `/api/${parentEntityType}s/${parentId}/${entityType}s`
    : `/api/${entityType}s`

  return useMutation({
    mutationFn: (data: RecursivePartial<T>) => {
      const transformedData: Record<string, unknown> = {}
      Object.entries(data).forEach(([key, value]) => {
        if (stringifyValues.includes(key as keyof T)) {
          transformedData[key] = JSON.stringify(value)
        } else {
          transformedData[key] = value
        }
      })

      return id
        ? putRequest(`${baseUrl}/${id}`, { [entityType]: transformedData })
        : postRequest(`${baseUrl}`, { [entityType]: transformedData })
    },
    onSuccess: (data: RecursivePartial<T>) => {
      queryClient.setQueryData(
        [entityType, { [queryKey]: queryKeyValue }],
        data,
      )
      if (showSuccessToast) {
        toast.success(t('shared.changes-saved'))
      }
      onSuccess?.(data)
    },
    onError: (error) => displayErrorToast(error, t),
  })
}
